import {
  Image,
  StyleSheet,
  View,
  type ImageSourcePropType,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { colors } from '@/constants/theme';

type Combo = 'deepOnFrost' | 'frostOnDeep' | 'frostOnElectric' | 'electricOnDeep' | 'electricOnCharcoal';

export interface BrandTileProps {
  /** Tile size in points; monogram is auto-sized to ~57% of this. */
  size?: number;
  /**
   * Color combo from brand kit page 10. Defaults to `frostOnDeep` — the
   * classic Deep Green tile + Frost monogram, the most common brand
   * presentation. Forbidden combos (Cool Blue + Electric Green, Deep Green
   * + Charcoal per page 11) aren't reachable here by design.
   */
  combo?: Combo;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
}

const FROST_MONOGRAM: ImageSourcePropType = require('@/assets/logo/MarketPryce-MonogramLogo-Frost.png');
const DEEP_MONOGRAM: ImageSourcePropType = require('@/assets/logo/MarketPryce-MonogramLogo-DeepGreen.png');
const CHARCOAL_MONOGRAM: ImageSourcePropType = require('@/assets/logo/MarketPryce-MonogramLogo-Charcoal.png');

const COMBOS: Record<Combo, { background: string; monogram: ImageSourcePropType }> = {
  deepOnFrost: { background: colors.frost, monogram: DEEP_MONOGRAM },
  frostOnDeep: { background: colors.deepGreen, monogram: FROST_MONOGRAM },
  frostOnElectric: { background: colors.electricGreen, monogram: FROST_MONOGRAM },
  electricOnDeep: { background: colors.deepGreen, monogram: DEEP_MONOGRAM }, // Electric green M would need that asset; falls back
  electricOnCharcoal: { background: colors.charcoal, monogram: CHARCOAL_MONOGRAM },
};

/**
 * Brand mark in its canonical container. Per brand kit page 10, the monogram
 * is never used floating bare on a surface — always inside a rounded-square
 * tile. 12px radius is mandatory (handoff app.css `mp-brandtile`).
 *
 * Spacing requirement (brand kit page 6): callers must leave clearance equal
 * to the M-width (~57% of `size`) around the tile.
 */
export function BrandTile({
  size = 56,
  combo = 'frostOnDeep',
  style,
  accessibilityLabel = 'MarketPryce',
}: BrandTileProps) {
  const { background, monogram } = COMBOS[combo];
  const monoSize = Math.round(size * 0.57);

  return (
    <View
      style={[
        styles.tile,
        {
          width: size,
          height: size,
          backgroundColor: background,
        },
        style,
      ]}
    >
      <Image
        source={monogram}
        style={{ width: monoSize, height: monoSize }}
        resizeMode="contain"
        accessibilityLabel={accessibilityLabel}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  tile: {
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
